/**
 * @fileoverview Input/output schemas for the chapter-drafter-v2 agent.
 *
 * This agent drafts a full chapter based on extensive context.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.scenes.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  // `draftingGenreGuidance` is consumed by the drafting domain system prompt
  // (`[[[draftingGenreLens]]]`), not the user prompt template, so it appears
  // unused to the prompt-template validator. It must remain in the input
  // schema so `buildAgentContext` populates it for the system prompt.
  ...AGENT_SCHEMA_OBJECTS.draftingGenreGuidance.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.openPlotThreads.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.activeContinuityFacts.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.previousChapterTail.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.writingStyleSample.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.writingBlocklist.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
}).partial({
  scenes: true,
  nextChapterContext: true,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for content-producing agents.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining the creative choices, changes made, or approach taken (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The generated or revised prose, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
